import React, { useEffect } from 'react';
import { getBrandName } from '../utils/brand';
import CopyableUrl from './CopyableUrl';

// `c` wraps inline code consistently across this page.
const c = text => <code>{text}</code>;

// Brand-specific install details. The docs site serves both brands by hostname, so the package
// name, config key, env var, tool prefix, and repo/npm links all switch with the brand.
// Note: GreenLine's npm package is `greenline-works-mcp-server` (plain `greenline-mcp-server` is
// taken on npm by an unrelated product), while its GitHub repo is `greenline-mcp-server`.
const BRAND_MCP = {
    GreenLine: {
        pkg: 'greenline-works-mcp-server',
        serverKey: 'greenline',
        env: 'GREENLINE_API_KEY',
        prefix: 'greenline_',
        remote: 'https://mcp.greenline.works/mcp',
        github: 'https://github.com/greenline-works/greenline-mcp-server',
        npm: 'https://www.npmjs.com/package/greenline-works-mcp-server',
    },
    'Kanban Zone': {
        pkg: 'kanban-zone-mcp-server',
        serverKey: 'kanbanzone',
        env: 'KANBANZONE_API_KEY',
        prefix: 'kanbanzone_',
        remote: 'https://mcp.kanbanzone.io/mcp',
        github: 'https://github.com/kanbanzone/kanban-zone-mcp-server',
        npm: 'https://www.npmjs.com/package/kanban-zone-mcp-server',
    },
};

// Single tool entry: name + one-line description + an example prompt the user might type.
const Tool = ({ name, description, example }) => (
    <li className="mcp-tool">
        <code className="mcp-tool-name">{name}</code>
        <span className="mcp-tool-desc"> — {description}</span>
        <div className="mcp-tool-example">
            <em>Try:</em> "{example}"
        </div>
    </li>
);

// Tool names are stored unprefixed; the brand prefix is applied at render. Examples may contain a
// `{brand}` token that is substituted with the brand name.
const TOOL_GROUPS = [
    {
        title: 'Organization & Auth',
        tools: [
            { name: 'get_me', description: 'Verify the connection works and return the organization name.', example: 'Is my {brand} connection working?' },
            { name: 'get_organization', description: 'Fetch the org profile, optionally including members, boards, columns, labels, and custom fields.', example: 'List every board with its columns.' },
        ],
    },
    {
        title: 'Boards',
        tools: [
            { name: 'list_boards', description: 'List all boards in the organization with optional related data.', example: 'What boards do I have?' },
            { name: 'get_board', description: 'Fetch a single board by its publicId.', example: 'Show me the OeMrbG8g board with its columns.' },
            { name: 'list_board_columns', description: 'List columns on a board, with optional title filter.', example: 'List columns on the OeMrbG8g board.' },
            { name: 'list_board_labels', description: 'List labels available on a board.', example: 'What labels are available on this board?' },
            { name: 'list_board_members', description: 'List members who can be assigned cards on a board.', example: 'Who can I assign cards to on this board?' },
            { name: 'list_board_custom_fields', description: 'List custom fields configured on a board.', example: 'What custom fields does this board have?' },
        ],
    },
    {
        title: 'Cards',
        tools: [
            { name: 'create_card', description: 'Create a single card on a board.', example: 'Create a card titled "Refactor auth" on board OeMrbG8g.' },
            { name: 'list_cards', description: 'List cards on a board with filters and pagination.', example: 'What cards are in progress on the OeMrbG8g board?' },
            { name: 'get_card', description: 'Fetch one card by its ObjectId.', example: 'Show me card 6700aabbccddeeff00112233.' },
            { name: 'search_cards', description: 'Full-text search cards by title or number, across the org or one board.', example: 'Find cards mentioning "billing".' },
            { name: 'update_card', description: 'Update fields on an existing card.', example: 'Reassign card 670... to bob@example.com.' },
            { name: 'move_card', description: 'Move a card to a different column (and optionally a different board).', example: 'Move card 670... to the In Progress column.' },
            { name: 'get_card_history', description: 'Fetch the activity history for a card from a given date.', example: 'What happened to card 670... since 2025-01-01?' },
            { name: 'get_card_metrics', description: 'Fetch time-in-column and cycle/lead-time metrics for a card.', example: 'How long has card 670... been in each column?' },
        ],
    },
    {
        title: 'Comments',
        tools: [
            { name: 'create_comment', description: 'Add a comment to a card.', example: 'Add a comment to card 670... saying: blocked on design review.' },
            { name: 'list_card_comments', description: 'List all comments on a card, oldest first.', example: 'What have people said on card 670...?' },
        ],
    },
    {
        title: 'Checklists',
        tools: [
            { name: 'create_checklist', description: 'Create a checklist on a card, optionally pre-populated with tasks (position is taken from array index).', example: 'Add a checklist to card 670... with tasks: write tests, update docs.' },
            { name: 'update_checklist', description: 'Update title or position of a checklist.', example: 'Rename checklist 670... to "QA tasks".' },
            { name: 'list_card_checklists', description: 'List all checklists on a card, including their tasks.', example: "What checklists are on card 670... and what's left to do?" },
        ],
    },
    {
        title: 'Tasks',
        tools: [
            { name: 'create_task', description: 'Add a task to an existing checklist.', example: 'Add a task "Update README" to checklist 670...' },
            { name: 'update_task', description: 'Update fields on an existing task — including completion.', example: 'Mark task 670... as complete.' },
            { name: 'move_task', description: 'Move a task between checklists or reorder within one.', example: 'Move task 670... from checklist A to checklist B at position 0.' },
        ],
    },
    {
        title: 'Labels',
        tools: [
            { name: 'create_label', description: 'Create a label on a board.', example: 'Add a red "Blocked" label to board OeMrbG8g.' },
            { name: 'get_label', description: 'Fetch a single label by its id.', example: 'Show me label 670...' },
            { name: 'update_label', description: "Update a label's name or color.", example: 'Rename label 670... to "Needs review".' },
        ],
    },
];

const TOOL_COUNT = TOOL_GROUPS.reduce((n, group) => n + group.tools.length, 0);

const MCPServer = () => {
    const brandName = getBrandName();
    const cfg = BRAND_MCP[brandName] || BRAND_MCP['Kanban Zone'];
    useEffect(() => {
        document.title = `MCP Server · ${brandName} Docs`;
    }, [brandName]);

    return (
        <div className="swagger-ui doc-page">
            <div className="information-container wrapper">
                <div className="info">
                    <h2 className="title">{brandName} MCP Server</h2>
                    <p>
                        Connect AI assistants like Claude Desktop, Claude Code, Cursor, VS Code, and
                        Windsurf directly to your {brandName} workspace via the{' '}
                        <a
                            className="link"
                            rel="noopener noreferrer"
                            target="_blank"
                            href="https://modelcontextprotocol.io/"
                        >
                            Model Context Protocol
                        </a>
                        . The server wraps the public API and exposes {TOOL_COUNT} tools for managing
                        boards, cards, comments, checklists, tasks, and labels.
                    </p>

                    <h2 id="overview">Overview</h2>
                    <p>
                        There are two ways to connect. The <strong>hosted server</strong> is a URL
                        you paste into your assistant — nothing to install, and you sign in with your{' '}
                        {brandName} account instead of copying an API key around. It is the
                        recommended option for every client on this page, Claude Desktop included.
                        The <strong>local install</strong> runs the same server as a Node.js process
                        on your own machine over stdio, for clients and environments that can't use a
                        remote URL. Either way it wraps the public API and exposes the tools below to
                        read and modify your workspace.
                    </p>
                    <h3>What you can do</h3>
                    <ul className="circle">
                        <li>Orchestrate workflows — create cards, move them between columns, assign owners.</li>
                        <li>Build reports — pull cycle-time, history, and column metrics into a summary.</li>
                        <li>Draft cards from research — turn meeting notes or specs into a checklist.</li>
                        <li>Manage checklists and tasks — including bulk task creation in one call.</li>
                        <li>Query board state — labels, members, custom fields, columns.</li>
                    </ul>
                    <h3>Supported clients</h3>
                    <ul className="circle">
                        <li><a className="link" href="#claude-desktop">Claude Desktop</a></li>
                        <li><a className="link" href="#claude-code">Claude Code</a></li>
                        <li><a className="link" href="#cursor">Cursor</a></li>
                        <li><a className="link" href="#vs-code">VS Code</a></li>
                        <li><a className="link" href="#windsurf">Windsurf</a></li>
                        <li><a className="link" href="#generic">Any other MCP client</a></li>
                    </ul>
                    <h3>Authentication</h3>
                    <p>
                        There are two ways to authenticate, and they differ in <em>whose</em> access
                        the connection carries — one person's, or the whole organization's.
                    </p>
                    <p>
                        <strong>Sign in — a user-level connection (recommended).</strong> The hosted
                        server lets you sign in with your {brandName} account: your client opens a
                        browser, you approve access, and it stores an access token for that client. The connection
                        acts as <em>you</em> — it reaches what your account can reach in the
                        organization you approved, and the cards, comments, and tasks it creates are
                        attributed to your name. There is no API key to paste into a config file or
                        rotate by hand, and disconnecting the client affects nobody else.
                    </p>
                    <p>
                        <strong>API key — an org-level connection.</strong> A composite key generated
                        in <strong>Settings → Integrations → API Keys</strong> represents the
                        organization, not a person. By default it reaches every board in the org
                        (keys can be scoped to chosen boards when generated), and changes made with
                        it are attributed to the organization's integration identity rather than to
                        any member. Both servers accept it: the hosted server as{' '}
                        {c('Authorization: Bearer accessId:apiKey')}, the local server via the{' '}
                        {c(cfg.env)} environment variable.
                    </p>
                    <p>
                        Rule of thumb: a person connecting their own assistant should sign in;
                        unattended, org-wide automation — CI, scripts, shared bots — should use an
                        API key. Neither method ever uses your password.
                    </p>
                    <h3>Admin controls</h3>
                    <p>
                        Organization admins govern MCP under{' '}
                        <strong>Settings → AI → MCP</strong>: an organization-wide switch, plus
                        per-member access. Everything starts off — an admin must turn MCP on for
                        the organization and grant each member before anyone can connect. Enabling
                        it records the admin's confirmation that the organization has an internal
                        AI use policy, and that attestation history stays visible in settings.
                    </p>
                    <p>
                        The switches take effect immediately, including for connections that are
                        already signed in — a revoked member's next request is rejected. Organizations
                        where you don't have MCP access simply don't appear on the sign-in approval
                        screen. Every action made through MCP is recorded in card history as the
                        member <em>via</em> the AI client (for example "… via Claude"), so
                        AI-originated changes stay distinguishable from direct ones in the audit
                        trail.
                    </p>

                    <h3>Data access</h3>
                    <p>
                        How much a connected AI tool can see depends on how you connected. Signed
                        in, it sees what your account sees in the organization you approved. With an
                        org-level API key, it sees every board and card in the organization (unless
                        the key was scoped at generation) — not just the one you're working in.
                        Either way, {brandName} does not control what a connected client does with
                        that data — whether it retains, logs, or reuses it. Only connect AI tools you trust with
                        your workspace data. The server does not expose delete operations, but it can
                        read, create, and modify cards, comments, checklists, and tasks.
                    </p>
                    <h3>Rate limits</h3>
                    <p>
                        Calls go through the same public API as direct integrations, so any limits
                        that apply to direct API use apply here too. Typical assistant use is
                        nowhere near them.
                    </p>
                    <h2 id="setup">Setup</h2>
                    <h3 id="before-you-connect">Before you connect</h3>
                    <p>
                        An organization owner, or a paid licensed member with the{' '}
                        <strong>AI &amp; Integrations</strong> permission, must enable MCP access
                        in <strong>Organization Settings → AI → MCP</strong> and confirm that your
                        organization has an internal AI use policy. This is off by default for
                        every organization. Once enabled, an admin can also choose which team
                        members are allowed to connect.
                    </p>
                    <p>
                        Start with the <a className="link" href="#hosted">hosted server</a> — it is
                        the recommended option for every client on this page, Claude Desktop
                        included. Reach for the <a className="link" href="#local">local install</a>{' '}
                        only when something in your setup rules the hosted server out.
                    </p>

                    <h3>Which one should I use?</h3>
                    <p><strong>Use the hosted server</strong> — this covers most people — when:</p>
                    <ul className="circle">
                        <li>You want the shortest setup: paste a URL, sign in, done.</li>
                        <li>You'd rather sign in with your account than copy an API key into a config file.</li>
                        <li>You want new tools and fixes automatically, with nothing to update on your machine.</li>
                        <li>You're on Claude Desktop, Claude Code, Cursor, VS Code, or Windsurf — all of them support remote MCP servers.</li>
                    </ul>
                    <p><strong>Use the local install</strong> when:</p>
                    <ul className="circle">
                        <li>Your client speaks only stdio and cannot connect to a remote URL.</li>
                        <li>You are running unattended — CI, a cron job, a script — where a browser sign-in is not possible and an API key in an environment variable is simpler.</li>
                        <li>Your network or policy blocks outbound access to {c(cfg.remote)}, or requires the process to run on your own hardware.</li>
                        <li>You are developing against your own fork or a local build of the server.</li>
                    </ul>

                    <h3 id="hosted">Hosted server (recommended)</h3>
                    <CopyableUrl url={cfg.remote} />
                    <p>
                        That one URL is the whole setup. The hosted server speaks Streamable HTTP,
                        there is nothing to install, and nothing to keep up to date — you always get
                        the current tool set.
                    </p>
                    <p>
                        Connect it by <strong>signing in with your {brandName} account</strong>: your
                        client opens a browser, you approve access, and it stores a token. If your
                        client cannot do that, or you are setting up something unattended, send an
                        API key in the {c('Authorization')} header instead. The per-client sections
                        below show both — <a className="link" href="#claude-desktop">Claude Desktop</a>,{' '}
                        <a className="link" href="#claude-code">Claude Code</a>,{' '}
                        <a className="link" href="#cursor">Cursor</a>,{' '}
                        <a className="link" href="#vs-code">VS Code</a>,{' '}
                        <a className="link" href="#windsurf">Windsurf</a>.
                    </p>

                    <h3 id="local">Local install (npx)</h3>
                    <p>
                        The local server exposes the same tools, running as a Node.js process on your
                        machine and talking to your client over stdio. Use it for the cases listed
                        above; otherwise prefer the hosted server.
                    </p>
                    <h4>Prerequisites</h4>
                    <ul className="circle">
                        <li>Node.js 18 or newer (for <code>npx</code>).</li>
                        <li>An API key from <strong>Settings → Integrations → API Keys</strong> — the local server has no sign-in flow.</li>
                    </ul>
                    <p>
                        The server is published to npm as{' '}
                        <a
                            className="link"
                            rel="noopener noreferrer"
                            target="_blank"
                            href={cfg.npm}
                        >
                            <code>{cfg.pkg}</code>
                        </a>
                        . Source on{' '}
                        <a
                            className="link"
                            rel="noopener noreferrer"
                            target="_blank"
                            href={cfg.github}
                        >
                            GitHub
                        </a>
                        . You don't need to install anything by hand — the snippets below use{' '}
                        <code>npx</code> to fetch and run it on demand.
                    </p>

                    <h3 id="claude-desktop">Claude Desktop</h3>
                    <p>
                        <strong>Hosted (recommended).</strong> Open{' '}
                        <strong>Settings → Connectors → Add custom connector</strong>, paste the
                        server URL, and click <strong>Connect</strong>:
                    </p>
                    <CopyableUrl url={cfg.remote} />
                    <p>
                        A browser window opens to sign in to {brandName} and approve access. The
                        tools then appear in Claude Desktop's tool picker — no config file to edit,
                        no API key to manage.
                    </p>
                    <p>
                        <strong>Local alternative.</strong> Edit your{' '}
                        <code>claude_desktop_config.json</code> — on macOS at{' '}
                        {c('~/Library/Application Support/Claude/claude_desktop_config.json')}, on
                        Windows at {c('%APPDATA%\\Claude\\claude_desktop_config.json')} — and add:
                    </p>
                    <pre><code>{`{
  "mcpServers": {
    "${cfg.serverKey}": {
      "command": "npx",
      "args": ["-y", "${cfg.pkg}"],
      "env": {
        "${cfg.env}": "accessId:apiKey"
      }
    }
  }
}`}</code></pre>
                    <p>Restart Claude Desktop. The {brandName} tools should appear in the tool picker.</p>

                    <h3 id="claude-code">Claude Code</h3>
                    <p><strong>Hosted (recommended).</strong> Add the server, then sign in when prompted:</p>
                    <pre><code>{`claude mcp add --transport http ${cfg.serverKey} ${cfg.remote}`}</code></pre>
                    <p>To authenticate with an API key instead of signing in, pass it as a header:</p>
                    <pre><code>{`claude mcp add --transport http ${cfg.serverKey} ${cfg.remote} \\
  --header "Authorization: Bearer accessId:apiKey"`}</code></pre>
                    <p><strong>Local alternative.</strong></p>
                    <pre><code>{`claude mcp add ${cfg.serverKey} \\
  -e ${cfg.env}=accessId:apiKey \\
  -- npx -y ${cfg.pkg}`}</code></pre>

                    <h3 id="cursor">Cursor</h3>
                    <p>
                        <strong>Hosted (recommended).</strong> Edit Cursor's <code>mcp.json</code> in
                        your settings directory and point it at the URL. Cursor prompts you to sign
                        in the first time it connects:
                    </p>
                    <pre><code>{`{
  "mcpServers": {
    "${cfg.serverKey}": {
      "url": "${cfg.remote}"
    }
  }
}`}</code></pre>
                    <p><strong>Local alternative.</strong></p>
                    <pre><code>{`{
  "mcpServers": {
    "${cfg.serverKey}": {
      "command": "npx",
      "args": ["-y", "${cfg.pkg}"],
      "env": { "${cfg.env}": "accessId:apiKey" }
    }
  }
}`}</code></pre>

                    <h3 id="vs-code">VS Code</h3>
                    <p><strong>Hosted (recommended).</strong></p>
                    <pre><code>{`code --add-mcp '{"name":"${cfg.serverKey}","url":"${cfg.remote}"}'`}</code></pre>
                    <p><strong>Local alternative.</strong></p>
                    <pre><code>{`code --add-mcp '{"name":"${cfg.serverKey}","command":"npx","args":["-y","${cfg.pkg}"],"env":{"${cfg.env}":"accessId:apiKey"}}'`}</code></pre>

                    <h3 id="windsurf">Windsurf</h3>
                    <p>
                        Open <strong>Settings → MCP servers → Add custom server</strong>. For the
                        hosted server — the recommended route — add a remote server entry with the
                        URL {c(cfg.remote)}. For a local install, paste the same JSON shape used for
                        Cursor.
                    </p>

                    <h3 id="generic">Any other MCP client</h3>
                    <p>
                        Clients that support remote servers can point straight at {c(cfg.remote)}{' '}
                        over Streamable HTTP, authenticating by sign-in or with an{' '}
                        {c('Authorization: Bearer accessId:apiKey')} header. Stdio-only clients run{' '}
                        {c(`npx -y ${cfg.pkg}`)} as a subprocess with {c(cfg.env)} set in the
                        environment. To run from source instead, clone the repo and run{' '}
                        {c('node src/index.js')} after {c('npm install')}.
                    </p>

                    <h2 id="tools">Tools Reference</h2>
                    <p>
                        {TOOL_COUNT} tools, all prefixed with <code>{cfg.prefix}</code>. Full
                        input/output schemas for the underlying endpoints live in the{' '}
                        <a className="link" href="/api">API Reference</a>. The list below documents
                        agent-facing usage with example prompts.
                    </p>
                    {TOOL_GROUPS.map(group => (
                        <section key={group.title} className="mcp-tool-group">
                            <h3>{group.title} ({group.tools.length})</h3>
                            <ul className="mcp-tool-list">
                                {group.tools.map(tool => (
                                    <Tool
                                        key={tool.name}
                                        name={`${cfg.prefix}${tool.name}`}
                                        description={tool.description}
                                        example={tool.example.replace('{brand}', brandName)}
                                    />
                                ))}
                            </ul>
                        </section>
                    ))}

                    <h2 id="faq">FAQ</h2>
                    <h3>Can I connect multiple organizations?</h3>
                    <p>
                        Yes — connect the server once per organization. Each connection reaches
                        exactly one organization: when you sign in, the approval screen asks which
                        of your organizations to connect, and the connection stays pinned to that
                        choice — switching organizations in the {brandName} app later does not
                        re-point it.
                    </p>
                    <p>
                        Add the hosted server once per organization and pick the matching
                        organization on each approval screen. If your client allows only one
                        connector per URL (Claude Desktop does), make each URL unique with a query
                        parameter — the server ignores it:
                    </p>
                    <pre><code>{`${cfg.remote}?org=acme
${cfg.remote}?org=widgets`}</code></pre>
                    <p>
                        Name each connector after its organization so they're easy to tell apart.
                        The parameter itself is only a label — the organization is still chosen on
                        the approval screen, so pick the one matching the name.
                    </p>
                    <p>
                        With the local install, create one entry per organization, each with that
                        organization's API key — keys are per-organization. And a single existing
                        connection can be moved between organizations at any time: disconnect the
                        server in your client, connect again, and pick the other organization on
                        the approval screen.
                    </p>

                    <h3>Troubleshooting</h3>
                    <ul className="circle">
                        <li><strong>401 errors on every tool call</strong> — if you connected with an API key, it is wrong or expired; regenerate it. If you signed in, disconnect the server in your client and connect again for a fresh token.</li>
                        <li><strong>"MCP access is turned off for this organization" or "…has not granted you MCP access"</strong> — an organization admin has MCP switched off, or you haven't been granted access. Ask an admin to enable it under <strong>Settings → AI → MCP</strong>; the change applies on your next request, no reconnect needed.</li>
                        <li><strong>The hosted server won't connect</strong> — check that your client supports remote MCP servers and that the URL is exactly {c(cfg.remote)}. If your network blocks it, use the <a className="link" href="#local">local install</a> instead.</li>
                        <li><strong>Tools don't appear (local install)</strong> — run <code>{`npx -y ${cfg.pkg}`}</code> once manually to confirm it downloads cleanly, then restart your client.</li>
                        <li><strong>"Card does not exist" on a card you can see</strong> — it's likely a mirror. Pass the <code>board</code> publicId to disambiguate.</li>
                        <li><strong>Truncation warnings on <code>list_cards</code></strong> — paginate with <code>page</code>/<code>count</code>, or narrow with filters.</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default MCPServer;
